import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { PrinterTypes } from '../constants/printer-types';
import { StorageNames } from '../constants/storage';
import { dateOnlyToDate } from '../helpers/date-helper';
import { db } from '../models/offline-db';
import { NetworkService } from '../services/network-service.service';
import { readLocalStorage } from '../services/session-manager';

const WEB_AUTHN_LOGIN_CACHE_PATHS = [
  '/api/webauthn/login-options',
  '/api/webauthn/login-verify',
];
const WEB_AUTHN_REGISTER_CACHE_PATHS = [
  '/api/webauthn/register-options',
  '/api/webauthn/register-verify',
];


@Injectable()
export class MyInterceptor implements HttpInterceptor {
  constructor(private router: Router) { }
  readonly networkService = inject(NetworkService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const token = localStorage.getItem(StorageNames.Token);

    let headers = req.headers;
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    const timezoneOffset = new Date().getTimezoneOffset().toString();
    headers = headers.set('X-Referer-Path', window.location.pathname);
    headers = headers.set('X-Timezone-Offset', timezoneOffset);
    headers = headers.set('X-' + PrinterTypes.Etiquetas, readLocalStorage(PrinterTypes.Etiquetas) ?? '');
    headers = headers.set('X-' + PrinterTypes.Guias, readLocalStorage(PrinterTypes.Guias) ?? '');
    headers = headers.set('X-' + PrinterTypes.Predeterminada, readLocalStorage(PrinterTypes.Predeterminada) ?? '');


    if (environment.pwa) {
      headers = headers.set('ngsw-bypass', 'true');

    }
    const authReq = req.clone({
      headers,
      withCredentials: true,
      cache: 'no-store',
      transferCache: false,
    });

    const isOffline = !this.networkService.isOnline();
    const cacheResponse = isWebAuthnLoginRequest(authReq.url) && environment.pwa;

    if (cacheResponse && isOffline) {
      return from(this.getCachedResponse(authReq)).pipe(
        switchMap((cachedResponse) => {
          if (cachedResponse) {
            return of(cachedResponse);
          }

          return throwError(() => new HttpErrorResponse({
            status: 0,
            statusText: 'Offline and no cached response available',
            url: authReq.urlWithParams,
          }));
        })
      );
    }

    return next.handle(authReq).pipe(
      switchMap(async (response) => {
        const isHttpResponse = response instanceof HttpResponse;
        if (isHttpResponse) {
          const resp = response as HttpResponse<unknown>;
          if (cacheResponse) {
            await this.cacheResponse(authReq, resp);
          }
          response = resp.clone({ body: convertDates(resp.body) });
        }
        return response;
      }),
      catchError((error) => {
        if (cacheResponse && isOfflineHttpError(error)) {
          return from(this.getCachedResponse(authReq)).pipe(
            switchMap((cachedResponse) => {
              if (cachedResponse) {
                return of(cachedResponse);
              }

              return throwError(() => error);
            })
          );
        }

        return throwError(() => error);
      })
    );
  }

  private async cacheResponse(req: HttpRequest<unknown>, response: HttpResponse<unknown>) {
    const key = buildCacheKey(req);
    await db.webAuthnCache.delete(key); // Remove any existing entry with the same key before adding a new one
    await db.webAuthnCache.put({
      key: buildCacheKey(req),
      url: req.urlWithParams,
      method: req.method.toUpperCase(),
      requestBody: serializeBody(req.body),
      responseBody: response.body,
      status: response.status,
      updatedAt: Date.now(),
    });
  }

  private async getCachedResponse(req: HttpRequest<unknown>) {
    const cached = await db.webAuthnCache.get(buildCacheKey(req));
    if (!cached) {
      return null;
    }

    return new HttpResponse({
      body: convertDates(cloneValue(cached.responseBody)),
      status: cached.status || 200,
      statusText: 'OK (cached)',
      url: req.urlWithParams,
    });
  }
}

function isWebAuthnLoginRequest(url: string) {
  return WEB_AUTHN_LOGIN_CACHE_PATHS.some((path) => url.includes(path));
}

function isOfflineHttpError(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 0;
}

function buildCacheKey(req: HttpRequest<unknown>) {
  let id = '';;
  const body: any = req.body;
  if (body) {
    if (body.credentialJson) {
      const credentialJson = JSON.parse(body.credentialJson);
      id = credentialJson.id;
    }
  }
  return `${req.method.toUpperCase()}|${req.urlWithParams}|${id}`;
}

function serializeBody(body: unknown): string {
  if (body === null || body === undefined) {
    return '';
  }

  try {
    return JSON.stringify(body);
  } catch {
    return String(body);
  }
}

function cloneValue<T>(value: T): T {
  if (value === null || value === undefined) {
    return value;
  }

  try {
    return structuredClone(value);
  } catch {
    try {
      return JSON.parse(JSON.stringify(value)) as T;
    } catch {
      return value;
    }
  }
}

function convertDates(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(obj)) {
    const date = new Date(obj);
    if (!isNaN(date.getTime())) return date;
  }

  if (typeof obj === 'string' && /^\d{4}-\d{2}-\d{2}/.test(obj)) {
    const date = dateOnlyToDate(obj);
    if (!isNaN(date.getTime())) return date;
  }

  if (Array.isArray(obj)) return obj.map(convertDates);
  if (typeof obj === 'object') {
    for (const key of Object.keys(obj)) {
      obj[key] = convertDates(obj[key]);
    }
  }
  return obj;
}
